import axios, { AxiosResponse } from 'axios'
import { computed, ComputedRef, onMounted, Ref, ref } from 'vue'

const DEV_DELAY = 400

const instance = axios.create({
	baseURL: 'https://app.dishformyrv.com/api/referrals',
})

type ApiStatus = 'Inactive' | 'Loading' | 'Completed' | 'Invalid'

// 	Capture Visit
export function useRecordVisits(): void {
	if (process.env.NODE_ENV !== 'production') {
		return
	}

	onMounted(() => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		instance.post('/visit')
	})
}

// 	Applies to Program
export function useApplyToProgram(): {
	email: Ref<string>,
	orderNumber: Ref<string>,
	isStatus: (status: ApiStatus) => ComputedRef<boolean>,
	apply: () => void,
} {
	const email = ref('')
	const orderNumber = ref('')

	const apiStatus: Ref<ApiStatus> = ref('Inactive')

	if (process.env.NODE_ENV !== 'production') {
		return {
			email,
			orderNumber,

			isStatus: (status: ApiStatus): ComputedRef<boolean> => computed(() => apiStatus.value === status),

			apply: (): void => {
				apiStatus.value = 'Loading'
				setTimeout(
					() => {
						apiStatus.value = 'Completed'
					},
					DEV_DELAY,
				)
			},
		}
	}

	return {
		email,
		orderNumber,

		isStatus: (status: ApiStatus): ComputedRef<boolean> => computed(() => apiStatus.value === status),

		apply: (): void => {
			apiStatus.value = 'Loading'

			instance.post(
				'/apply',
				{
					email: email.value,
					orderNumber: orderNumber.value,
				},
			).then(() => {
				apiStatus.value = 'Completed'
			}).catch(() => {
				apiStatus.value = 'Invalid'
			})
		},
	}
}

//	Validate the Referral ID info
export async function useReferralInformation(id: string): Promise<void> {
	if (process.env.NODE_ENV !== 'production') {
		await new Promise(resolve => setTimeout(
			resolve,
			DEV_DELAY,
		))

		return
	}

	try {
		await instance.get(`/${id}`)
	} catch {
		window.location.href = 'https://www.dishformyrv.com'
		await new Promise(() => {})
	}
}

//	Claim a coupon
export function useClaimCoupon(id: string): { email: Ref<string>, couponCode: Ref<string>, isStatus: (status: ApiStatus) => ComputedRef<boolean>, claim: () => void } {
	const email = ref('')
	const couponCode = ref('')

	const apiStatus: Ref<ApiStatus> = ref('Inactive')

	if (process.env.NODE_ENV !== 'production') {
		return {
			email,
			couponCode,

			isStatus: (status: ApiStatus): ComputedRef<boolean> => computed(() => apiStatus.value === status),

			claim: (): void => {
				apiStatus.value = 'Loading'
				setTimeout(
					() => {
						apiStatus.value = 'Completed'
						couponCode.value = 'FAKECOUPONCODE'
					},
					DEV_DELAY,
				)
			},
		}
	}

	return {
		email,
		couponCode,

		isStatus: (status: ApiStatus): ComputedRef<boolean> => computed(() => apiStatus.value === status),

		// status: readonly(status),

		claim: (): void => {
			apiStatus.value = 'Loading'

			instance.post(
				`/${id}/coupons`,
				{
					email: email.value,
				},
			).then((response: AxiosResponse<{code: string}>) => {
				apiStatus.value = 'Completed'
				couponCode.value = response.data.code
			}).catch(() => {
				apiStatus.value = 'Invalid'
			})
		},
	}
}
